import React, { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import AppLinks from 'src/common/components/AppLinks';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import { NEXT_STATIC_IMAGES, SHARED_MEDIA_QUERIES as sharedMediaQueries } from 'src/common/constants';
import Image from 'next/image';
import Carousel from 'src/common/components/dockyard/Carousel';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import classNames from 'classnames';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import styles from './HowItWorks.module.scss';
import getClassNames from '../../utils/getClassNames';

export type HowItWorksProps = {
  headingColor?: 'neutral700' | 'midnight01';
  headingSize: 'heading-3' | 'display-5';
};

const HowItWorks: FC<HowItWorksProps> = ({ headingColor = 'neutral700', headingSize }) => {
  const [t] = useTranslation('how-it-works-section');
  const { isMobileNativeWebView } = useMobileNativeWebView();
  const cards: {
    description: string;
    icon: 'browse' | 'inquire' | 'enjoy';
    title: string;
  }[] = useMemo(() => [
    {
      description: t('how-it-works--card-one-description'),
      icon: 'browse',
      title: t('how-it-works--card-one-title'),
    },
    {
      description: t('how-it-works--card-two-description'),
      icon: 'inquire',
      title: t('how-it-works--card-two-title'),
    },
    {
      description: t('how-it-works--card-three-description'),
      icon: 'enjoy',
      title: t('how-it-works--card-three-title'),
    },
  ], [t]);

  return (
    <PitchPageLayout backgroundColor="bluminescence100">
      <div className={getClassNames(styles, 'header', classNames(headingColor, {
        isDisplayHeading: headingSize === 'display-5',
      }))}
      >
        <Typography
          type="h2"
          size={headingSize}
          weight="semibold"
        >
          {t('how-it-works--section-title')}
        </Typography>
        <div className={styles.stats}>
          <Icon id="star-filled" size="m" />
          <Typography
            size="body-xl"
            type="span"
            weight="regular"
          >
            {' '}
            {t('how-it-works--rating')}
          </Typography>
        </div>
      </div>
      <div className={styles.cardList}>
        <CarouselProvider
          emblaOptions={{
            active: true,
            breakpoints: {
              [sharedMediaQueries.m]: {
                active: false,
              },
            },
          }}
        >
          <Carousel>
            {cards.map((card) => (
              <div key={card.title} className={styles.cardListItem}>
                <div className={styles.imageWrap}>
                  <Image
                    src={`${NEXT_STATIC_IMAGES}dlp/how-it-works/${card.icon}.svg`}
                    alt={card.title}
                    fill
                  />
                </div>
                <Typography
                  className={styles.cardTitle}
                  size="body-xl"
                  type="h3"
                  weight="semibold"
                >
                  {card.title}
                </Typography>
                <Typography
                  className={styles.cardDescription}
                  size="body-m"
                  type="p"
                  weight="regular"
                >
                  {card.description}
                </Typography>
              </div>
            ))}
          </Carousel>
        </CarouselProvider>
      </div>
      {!isMobileNativeWebView && (
        <div className={styles.appLinksWrapper}>
          <AppLinks
            classNameModifier="pitchPageHowItWorks"
            outline="midnight"
          />
        </div>
      )}
    </PitchPageLayout>
  );
};

export default HowItWorks;
